<template>
  <div class="weekly-wbs-groups" :class="{ 'weekly-wbs-groups--root': depth === 0 }">
    <div v-if="isLoading" class="py-6 text-center text-sm text-gray-500">
      {{ $t('loading') }}...
    </div>

    <div v-else-if="!listGroups.length" class="py-6 text-center text-sm text-gray-500">
      {{ $t('no_results') }}
    </div>

    <div v-else class="weekly-wbs-groups__list">
      <div
        v-for="group in listGroups"
        :key="group.id"
        class="weekly-wbs-groups__item"
      >
        <div
          class="weekly-wbs-groups__row"
          :class="rowClass(group)"
          :style="rowStyle(group)"
          @click="toggleGroup(group)"
        >
          <button
            v-if="canToggleDetail(group)"
            type="button"
            class="weekly-wbs-groups__expand-btn"
            :aria-expanded="isExpanded(group)"
            @click.stop="toggleGroup(group)"
          >
            <b-icon
              pack="fas"
              :icon="isExpanded(group) ? 'chevron-down' : 'chevron-right'"
              size="is-small"
            />
          </button>
          <span v-else class="weekly-wbs-groups__expand-spacer" />

          <span class="weekly-wbs-groups__level-badge" :class="levelBadgeClass">
            {{ levelBadgeLabel }}
          </span>

          <div class="weekly-wbs-groups__content">
            <span v-if="group.code" class="weekly-wbs-groups__code">{{ group.code }}</span>
            <span class="weekly-wbs-groups__name">{{ group.name }}</span>
            <span
              v-if="group.activities_count != null && Number(group.activities_count) > 0"
              class="weekly-wbs-groups__activities-count"
            >
              ({{ Number(group.activities_count) }})
            </span>
          </div>

          <div class="weekly-wbs-groups__metrics" @click.stop>
            <div class="weekly-wbs-groups__progress">
              <span class="weekly-wbs-groups__progress-label">{{ $t('progress') }}</span>
              <span class="weekly-wbs-groups__progress-track" aria-hidden="true">
                <span
                  class="weekly-wbs-groups__progress-fill"
                  :style="{ width: groupProgressBarWidth(group) }"
                />
              </span>
              <span class="weekly-wbs-groups__progress-value">{{ groupProgressDisplay(group) }}</span>
            </div>

            <span class="weekly-wbs-groups__metric">
              <span class="weekly-wbs-groups__metric-label">{{ $t('budget') }}</span>
              <span class="weekly-wbs-groups__metric-value">{{ groupBudgetDisplay(group) }}</span>
            </span>
            <span class="weekly-wbs-groups__metric">
              <span class="weekly-wbs-groups__metric-label">{{ $t('earned_value') }}</span>
              <span class="weekly-wbs-groups__metric-value">{{ groupEarnedValueDisplay(group) }}</span>
            </span>
            <span class="weekly-wbs-groups__metric">
              <span class="weekly-wbs-groups__metric-label">{{ $t('incurred_cost') }}</span>
              <span class="weekly-wbs-groups__metric-value">{{ groupIncurredCostDisplay(group) }}</span>
            </span>

            <button
              v-if="canCreateActivity(group)"
              type="button"
              class="weekly-wbs-groups__create-btn"
              @click="openCreateActivity(group)"
            >
              <b-icon pack="fas" icon="plus" size="is-small" />
              <span>{{ $t('add_activity') }}</span>
            </button>
          </div>
        </div>

        <div v-if="isExpanded(group)" class="weekly-wbs-groups__children">
          <weekly-wbs-groups
            v-if="group.children_count > 0"
            :project-id="projectId"
            :parent-id="group.id"
            :depth="depth + 1"
            :parent-color="groupColor(group)"
            :filters-from-root="effectiveFiltersFromRoot"
            :week-start-date="weekStartDate"
            :week-end-date="weekEndDate"
            :week-number="weekNumber"
            :hidden-column-groups="hiddenColumnGroups"
            :filter-subactivities-by-week="filterSubactivitiesByWeek"
            :reschedule-mode="rescheduleMode"
            :edited-subactivities="editedSubactivities"
            :activity-filters="activityFilters"
            :selected-discipline-id="selectedDisciplineId"
            @update-rescheduled-activity="$emit('update-rescheduled-activity', $event)"
            @remove-rescheduled-activity="$emit('remove-rescheduled-activity', $event)"
          />
          <div v-else-if="hasActivities(group)" class="weekly-wbs-groups__activities-panel">
            <activities-weekly-table
              :key="'group-activities-' + group.id + '-' + activitiesTableKey(group)"
              :project-id="projectId"
              :group-id="group.id"
              activities-load-mode="group"
              embedded
              :week-start-date="weekStartDate"
              :week-end-date="weekEndDate"
              :week-number="weekNumber"
              :hidden-column-groups="hiddenColumnGroups"
              :filter-subactivities-by-week="filterSubactivitiesByWeek"
              :reschedule-mode="rescheduleMode"
              :edited-subactivities="editedSubactivities"
              :activity-filters="activityFilters"
              :selected-discipline-id="selectedDisciplineId"
              @update-rescheduled-activity="$emit('update-rescheduled-activity', $event)"
              @remove-rescheduled-activity="$emit('remove-rescheduled-activity', $event)"
            />
          </div>
        </div>
      </div>
    </div>

    <edit-activity-modal
      ref="editActivityModal"
      :project-id="effectiveProjectId"
      @created="onEditActivityModalCreated"
    />
  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import ActivitiesWeeklyTable from './activities-weekly-table.vue'
import EditActivityModal from './edit-activity-modal.vue'
import {
  mergeWeeklyUiSessionSection,
  readWeeklyUiSession,
} from './weekly-ui-session.js'

const DEFAULT_GROUP_COLOR = '#3b66bc'

export default {
  name: 'weekly-wbs-groups',

  components: {
    ActivitiesWeeklyTable,
    EditActivityModal,
  },

  props: {
    projectId: {
      type: Number,
      default: null,
    },
    parentId: {
      type: Number,
      default: null,
    },
    depth: {
      type: Number,
      default: 0,
    },
    parentColor: {
      type: String,
      default: null,
    },
    weekStartDate: {
      type: String,
      default: null,
    },
    weekEndDate: {
      type: String,
      default: null,
    },
    weekNumber: {
      type: Number,
      default: null,
    },
    /** Filtros de la raíz (visibilidad usuario); los subniveles los heredan como activity-groups. */
    filtersFromRoot: {
      type: Object,
      default: null,
    },
    /** Grupos de columnas ocultos (controlados desde weekly-view-wbs). */
    hiddenColumnGroups: {
      type: Array,
      default: () => [],
    },
    /** Si true, las subactividades se filtran por semana seleccionada. */
    filterSubactivitiesByWeek: {
      type: Boolean,
      default: true,
    },
    rescheduleMode: {
      type: Boolean,
      default: false,
    },
    editedSubactivities: {
      type: Array,
      default: () => [],
    },
    activityFilters: {
      type: Object,
      default: () => ({}),
    },
    /** Disciplina BoqRoot seleccionada (null = Todas). */
    selectedDisciplineId: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      isLoading: false,
      listGroups: [],
      expandedGroupIds: [],
      generatedFilters: {},
      pendingCreateGroup: null,
      activitiesRefreshByGroup: {},
    }
  },

  computed: {
    ...mapGetters({
      project: 'app/project',
    }),

    effectiveProjectId() {
      return this.projectId || (this.project && this.project.id) || null
    },

    levelBadgeLabel() {
      if (this.depth === 0) {
        return this.$t('group')
      }
      return `${this.$t('subgroup')} ${this.depth}`
    },

    levelBadgeClass() {
      return this.depth === 0
        ? 'weekly-wbs-groups__level-badge--root'
        : 'weekly-wbs-groups__level-badge--subgroup'
    },

    effectiveFiltersFromRoot() {
      if (this.depth === 0) {
        return this.generatedFilters
      }
      return this.filtersFromRoot || {}
    },

    isTakeoffProgressMode() {
      const configs = this.project && this.project.configs
      return !!(configs && configs.progress_type_mode === 'takeoff')
    },
  },

  watch: {
    parentId() {
      this.loadGroups()
    },
    effectiveProjectId(projectId, previousProjectId) {
      if (projectId && projectId !== previousProjectId && this.depth === 0) {
        this.loadGroups()
      }
    },
    weekStartDate() {
      this.loadGroups()
    },
    weekEndDate() {
      this.loadGroups()
    },
    filterSubactivitiesByWeek() {
      this.loadGroups()
    },
    selectedDisciplineId() {
      this.loadGroups()
    },
  },

  created() {
    this.loadGroups()
  },

  methods: {
    // Filtros de activity-filters NO van a la API de grupos; solo a activities-weekly-table.
    buildRootFilters(projectId) {
      return {
        p: projectId,
        s_num: 1,
        with_groups: 1,
        with_assignments: 1,
        order_field: 'planning_code',
        order_dir: 'asc',
        per_page: 25,
        page: 1,
      }
    },

    /**
     * En takeoff + vista semanal: takeoff=1 y rango de la semana actual.
     * El backend amplía weekly_end +35 días (6 semanas) al filtrar actividades/grupos.
     * Vista general: sin estos params (comportamiento actual).
     * Con disciplina seleccionada: takeoff_discipline_id (stages bajo esa disciplina).
     */
    applyTakeoffWeeklyParams(params) {
      if (
        this.isTakeoffProgressMode
        && this.filterSubactivitiesByWeek
        && this.weekStartDate
        && this.weekEndDate
      ) {
        params.takeoff = 1
        params.weekly_start = this.weekStartDate
        params.weekly_end = this.weekEndDate
      } else {
        delete params.takeoff
        delete params.weekly_start
        delete params.weekly_end
      }

      if (this.isTakeoffProgressMode && this.selectedDisciplineId != null) {
        params.takeoff_discipline_id = this.selectedDisciplineId
      } else {
        delete params.takeoff_discipline_id
      }

      return params
    },

    buildGroupsRequestParams(projectId) {
      const params = this.depth === 0
        ? { ...this.buildRootFilters(projectId) }
        : { ...this.effectiveFiltersFromRoot }

      params.with_groups = 1
      this.applyTakeoffWeeklyParams(params)

      if (this.parentId !== null && this.parentId !== undefined) {
        params.parent_id = this.parentId
      }

      return params
    },

    async loadGroups() {
      const projectId = this.effectiveProjectId
      if (!projectId) {
        this.listGroups = []
        this.isLoading = false
        return
      }

      if (this.depth === 0) {
        this.generatedFilters = this.buildRootFilters(projectId)
      }

      this.isLoading = true

      const params = this.buildGroupsRequestParams(projectId)

      try {
        const { data } = await axios.get(`/api/v2/project/${projectId}/activity/groups`, { params })

        if (data && data.groups) {
          this.listGroups = this.depth === 0 ? data.groups : this.sortByCode(data.groups)
        } else {
          this.listGroups = []
          this.$notify.error('error_loading_activities')
        }
      } catch (error) {
        this.listGroups = []
        this.$notify.error('error_loading_activities')
      } finally {
        this.isLoading = false
        this.$nextTick(() => {
          this.restoreExpandedFromSession()
        })
      }
    },

    sortByCode(groups) {
      return groups.slice().sort((a, b) => {
        return (a.code || '').localeCompare(b.code || '', undefined, { numeric: true, sensitivity: 'base' })
      })
    },

    groupColor(group) {
      if (group.color) {
        return group.color
      }
      if (group.configs) {
        try {
          const configs = typeof group.configs === 'string' ? JSON.parse(group.configs) : group.configs
          return configs && configs.color ? configs.color : DEFAULT_GROUP_COLOR
        } catch (error) {
          return DEFAULT_GROUP_COLOR
        }
      }
      return DEFAULT_GROUP_COLOR
    },

    canExpand(group) {
      return Number(group.children_count || 0) > 0
    },

    hasActivities(group) {
      return Number(group.has_activities) === 1 || group.has_activities === true
    },

    canToggleDetail(group) {
      return this.canExpand(group) || this.hasActivities(group)
    },

    isLeaf(group) {
      return Number(group.children_count || 0) === 0
    },

    /** Solo se pueden crear actividades en grupos hoja (sin subgrupos). */
    canCreateActivity(group) {
      return this.isLeaf(group)
    },

    activitiesTableKey(group) {
      if (!group || group.id == null) {
        return 0
      }
      return this.activitiesRefreshByGroup[group.id] || 0
    },

    openCreateActivity(group) {
      if (!this.canCreateActivity(group) || !this.$refs.editActivityModal) {
        return
      }
      this.pendingCreateGroup = group
      this.$refs.editActivityModal.show({
        mode: 'create-activity',
        group,
      })
    },

    async onEditActivityModalCreated(payload) {
      const group = this.pendingCreateGroup
      const activityId = payload && payload.activity_id != null
        ? payload.activity_id
        : null
      this.pendingCreateGroup = null

      if (!group || activityId == null) {
        return
      }

      try {
        await this.attachActivityToGroup(group.id, activityId)
      } catch (error) {
        this.$notify.error('error_saving')
        return
      }

      this.$set(group, 'has_activities', 1)
      if (!this.isExpanded(group)) {
        this.expandedGroupIds = [...this.expandedGroupIds, group.id]
        this.persistExpandedSession()
      }
      const currentKey = this.activitiesRefreshByGroup[group.id] || 0
      this.$set(this.activitiesRefreshByGroup, group.id, currentKey + 1)
    },

    /**
     * Vincula la actividad al grupo con la API existente de link
     * (sin modificar el endpoint de creación de actividad).
     */
    async attachActivityToGroup(groupId, activityId) {
      const { data } = await axios.get('/api/v2/activities', {
        params: {
          p: this.effectiveProjectId,
          light: 1,
          group_id: groupId,
          per_page: 9999,
          page: 1,
        },
      })

      const ids = (data && Array.isArray(data.activities) ? data.activities : [])
        .map((activity) => activity.id)
        .filter((id) => id != null)

      if (!ids.includes(activityId)) {
        ids.push(activityId)
      }

      await axios.post(`/api/v2/activity/groups/${groupId}/link`, {
        activities: ids,
        entity_type: 'project',
      })
    },

    toggleGroup(group) {
      if (!this.canToggleDetail(group)) {
        return
      }
      if (this.isExpanded(group)) {
        this.expandedGroupIds = this.expandedGroupIds.filter((id) => id !== group.id)
      } else {
        this.expandedGroupIds = [...this.expandedGroupIds, group.id]
      }
      this.persistExpandedSession()
    },

    persistExpandedSession() {
      mergeWeeklyUiSessionSection(
        this.effectiveProjectId,
        'expanded',
        this.expandedGroupIds,
        this.listGroups.map((group) => group.id)
      )
    },

    restoreExpandedFromSession() {
      const stored = readWeeklyUiSession(this.effectiveProjectId).expanded
      if (!stored.length || !this.listGroups.length) {
        return
      }

      const validIds = new Set(this.listGroups.map((group) => group.id))
      this.expandedGroupIds = stored.filter((id) => validIds.has(id))
    },

    isExpanded(group) {
      return this.expandedGroupIds.includes(group.id)
    },

    rowClass(group) {
      return {
        'weekly-wbs-groups__row--root': this.depth === 0,
        'weekly-wbs-groups__row--subgroup': this.depth > 0,
        'weekly-wbs-groups__row--expanded': this.isExpanded(group),
        'weekly-wbs-groups__row--leaf': this.isLeaf(group),
      }
    },

    rowStyle(group) {
      const styles = {}
      const color = this.groupColor(group)

      if (this.depth === 0) {
        styles.backgroundColor = color
        return styles
      }

      styles.paddingLeft = `${0.75 + Math.min(this.depth - 1, 5) * 1.25}rem`
      styles.backgroundColor = this.parentColor
        ? this.lightenColor(this.parentColor, 0.88)
        : '#eef3fb'

      return styles
    },

    lightenColor(hex, amount) {
      const normalized = hex.replace('#', '')
      if (normalized.length !== 6) {
        return '#eef3fb'
      }

      const r = parseInt(normalized.slice(0, 2), 16)
      const g = parseInt(normalized.slice(2, 4), 16)
      const b = parseInt(normalized.slice(4, 6), 16)

      const mix = (channel) => Math.round(channel + (255 - channel) * amount)

      return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`
    },

    groupActivitiesCount(group) {
      if (group.activities_count != null) {
        return group.activities_count
      }
      if (group.has_activities != null) {
        return group.has_activities ? 1 : 0
      }
      return 0
    },

    // Progreso actual del grupo (porcentaje 0-100) tal y como lo devuelve getProgress() en backend.
    groupProgressCurrent(group) {
      const progress = group.group_progress || {}
      const value = Number(progress.progress_current_group)
      return Number.isFinite(value) ? value : 0
    },

    groupProgressDisplay(group) {
      return `${Math.round(this.groupProgressCurrent(group))}%`
    },

    groupProgressBarWidth(group) {
      const value = this.groupProgressCurrent(group)
      const clamped = Math.max(0, Math.min(100, value))
      return `${clamped}%`
    },

    // Presupuesto persistido en el grupo (puede ser null si no se ha importado/definido).
    groupBudgetRaw(group) {
      if (group.budget === null || group.budget === undefined || group.budget === '') {
        return null
      }
      const value = Number(group.budget)
      return Number.isFinite(value) ? value : null
    },

    groupBudgetDisplay(group) {
      const budget = this.groupBudgetRaw(group)
      return budget === null ? '—' : this.formatCurrencyAbbrev(budget)
    },

    // Valor ganado = progreso (%) / 100 * presupuesto.
    groupEarnedValueDisplay(group) {
      const budget = this.groupBudgetRaw(group)
      if (budget === null) {
        return '—'
      }
      return this.formatCurrencyAbbrev((this.groupProgressCurrent(group) / 100) * budget)
    },

    // Coste incurrido persistido en el grupo (puede ser null si no se ha importado/definido).
    groupIncurredCostRaw(group) {
      if (group.incurred_costs === null || group.incurred_costs === undefined || group.incurred_costs === '') {
        return null
      }
      const value = Number(group.incurred_costs)
      return Number.isFinite(value) ? value : null
    },

    groupIncurredCostDisplay(group) {
      const incurred = this.groupIncurredCostRaw(group)
      return incurred === null ? '—' : this.formatCurrencyAbbrev(incurred)
    },

    // Formato abreviado tipo 13.87M€ / 488.0K€ / 0€.
    formatCurrencyAbbrev(value) {
      const num = Number(value) || 0
      const abs = Math.abs(num)
      if (abs >= 1e6) {
        return `${(num / 1e6).toFixed(2)}M€`
      }
      if (abs >= 1e3) {
        return `${(num / 1e3).toFixed(1)}K€`
      }
      return `${Math.round(num)}€`
    },
  },
}
</script>

<style scoped>
.weekly-wbs-groups__list {
  display: flex;
  flex-direction: column;
}

.weekly-wbs-groups__item {
  min-width: 0;
}

.weekly-wbs-groups__row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 2rem;
  padding: 0.3125rem 0.75rem;
  border: 0;
  border-bottom: 1px solid #dbe6f5;
  border-radius: 0;
  cursor: pointer;
  background-color: #f8fafc;
  font-size: 0.75rem;
  line-height: 1.25;
  transition: filter 0.12s ease, box-shadow 0.12s ease;
}

.weekly-wbs-groups__row:hover {
  filter: brightness(0.97);
}

.weekly-wbs-groups__row--root {
  color: #fff;
  font-weight: 600;
  border-bottom-color: rgba(255, 255, 255, 0.18);
}

.weekly-wbs-groups__row--root:hover {
  filter: brightness(1.06);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.12);
}

.weekly-wbs-groups__row--subgroup {
  color: #1f2937;
  border-bottom-color: #dbe6f5;
}

.weekly-wbs-groups__row--subgroup:hover {
  filter: brightness(0.98);
  box-shadow: inset 0 0 0 1px rgba(59, 102, 188, 0.12);
}

.weekly-wbs-groups__expand-btn,
.weekly-wbs-groups__expand-spacer {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.125rem;
  height: 1.125rem;
  flex-shrink: 0;
}

.weekly-wbs-groups__expand-btn {
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  opacity: 0.9;
}

.weekly-wbs-groups__expand-btn:hover {
  opacity: 1;
}

.weekly-wbs-groups__level-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.0625rem 0.375rem;
  border-radius: 0.25rem;
  font-size: 0.625rem;
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  white-space: nowrap;
  flex-shrink: 0;
}

.weekly-wbs-groups__level-badge--root {
  background-color: rgba(255, 255, 255, 0.22);
  color: #fff;
}

.weekly-wbs-groups__level-badge--subgroup {
  background-color: rgba(59, 102, 188, 0.15);
  color: #1d4ed8;
}

.weekly-wbs-groups__content {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  min-width: 0;
  flex: 1;
}

.weekly-wbs-groups__code {
  flex-shrink: 0;
  font-size: 0.75rem;
  font-weight: 600;
  color: #3b66bc;
  white-space: nowrap;
  opacity: 0.95;
}

.weekly-wbs-groups__row--root .weekly-wbs-groups__code {
  color: rgba(255, 255, 255, 0.88);
}

.weekly-wbs-groups__name {
  font-size: 0.75rem;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-transform: none;
}

.weekly-wbs-groups__activities-count {
  flex-shrink: 0;
  font-size: 0.6875rem;
  font-weight: 500;
  color: #6b7280;
  white-space: nowrap;
}

.weekly-wbs-groups__row--root .weekly-wbs-groups__activities-count {
  color: rgba(255, 255, 255, 0.75);
}

.weekly-wbs-groups__metrics {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-left: auto;
  padding-left: 0.5rem;
  flex-shrink: 0;
  white-space: nowrap;
  cursor: default;
}

.weekly-wbs-groups__progress {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  min-width: 7.5rem;
  padding: 0.15rem 0.45rem;
  border-radius: 0.25rem;
  border: 1px solid rgba(17, 24, 39, 0.1);
  background-color: rgba(255, 255, 255, 0.55);
}

.weekly-wbs-groups__row--root .weekly-wbs-groups__progress {
  border-color: rgba(255, 255, 255, 0.28);
  background-color: rgba(255, 255, 255, 0.14);
}

.weekly-wbs-groups__progress-label {
  font-size: 0.6875rem;
  font-weight: 500;
  color: #6b7280;
}

.weekly-wbs-groups__row--root .weekly-wbs-groups__progress-label {
  color: rgba(255, 255, 255, 0.8);
}

.weekly-wbs-groups__progress-track {
  display: inline-block;
  width: 3.25rem;
  height: 0.375rem;
  border-radius: 9999px;
  background-color: rgba(17, 24, 39, 0.12);
  overflow: hidden;
  flex-shrink: 0;
}

.weekly-wbs-groups__row--root .weekly-wbs-groups__progress-track {
  background-color: rgba(255, 255, 255, 0.28);
}

.weekly-wbs-groups__progress-fill {
  display: block;
  height: 100%;
  border-radius: inherit;
  background-color: #22c55e;
  max-width: 100%;
}

.weekly-wbs-groups__row--root .weekly-wbs-groups__progress-fill {
  background-color: #86efac;
}

.weekly-wbs-groups__progress-value {
  font-size: 0.75rem;
  font-weight: 700;
  color: #111827;
  min-width: 2.25rem;
}

.weekly-wbs-groups__row--root .weekly-wbs-groups__progress-value {
  color: #fff;
}

.weekly-wbs-groups__metric {
  display: inline-flex;
  align-items: baseline;
  gap: 0.25rem;
  padding: 0.15rem 0.45rem;
  border-radius: 0.25rem;
  border: 1px solid rgba(17, 24, 39, 0.1);
  background-color: rgba(255, 255, 255, 0.55);
  font-size: 0.75rem;
  line-height: 1.25;
}

.weekly-wbs-groups__row--root .weekly-wbs-groups__metric {
  border-color: rgba(255, 255, 255, 0.28);
  background-color: rgba(255, 255, 255, 0.14);
}

.weekly-wbs-groups__metric-label {
  color: #6b7280;
  font-weight: 500;
  text-transform: none;
}

.weekly-wbs-groups__row--root .weekly-wbs-groups__metric-label {
  color: rgba(255, 255, 255, 0.72);
}

.weekly-wbs-groups__metric-value {
  color: #111827;
  font-weight: 600;
}

.weekly-wbs-groups__row--root .weekly-wbs-groups__metric-value {
  color: rgba(255, 255, 255, 0.95);
}

.weekly-wbs-groups__create-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  margin-left: 0.125rem;
  padding: 0.15rem 0.55rem;
  border-radius: 0.25rem;
  border: 1px solid rgba(255, 255, 255, 0.55);
  background: rgba(255, 255, 255, 0.95);
  color: #2c426c;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.25;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.12s ease, border-color 0.12s ease;
}

.weekly-wbs-groups__create-btn:hover {
  background: #fff;
  border-color: #fff;
}

.weekly-wbs-groups__row--subgroup .weekly-wbs-groups__create-btn {
  border-color: #c5d4eb;
  background: #fff;
  color: #2c426c;
}

.weekly-wbs-groups__row--subgroup .weekly-wbs-groups__create-btn:hover {
  background: #eef3fb;
  border-color: #3b66bc;
}

.weekly-wbs-groups__children {
  margin: 0;
  padding: 0;
  border: 0;
}

.weekly-wbs-groups__activities-panel {
  min-height: 0;
}
</style>
